import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { Subscription } from 'rxjs';

import {
  AcompanhamentoAluno,
  AcompanhamentoAnexo,
  AcompanhamentoComponenteCurricular,
  AcompanhamentoFormulario
} from '../models/acompanhamento-pedagogico.types';
import { AcompanhamentoPedagogicoAnexoApiService } from '../services/acompanhamento-pedagogico-anexo-api.service';
import { PrototypeSelectOption } from '@infrastructure/ui/components/forms/prototype-select/prototype-select.component';
import { SearchMultiselectOption } from '@infrastructure/ui/components/forms/search-multiselect/search-multiselect.component';
import { AnexoItem } from '@infrastructure/ui/components/anexos/anexos.component';

@Component({
  selector: 'app-acompanhamento-form',
  templateUrl: './acompanhamento-form.component.html',
  styleUrls: ['./acompanhamento-form.component.scss']
})
export class AcompanhamentoFormComponent implements OnChanges, OnDestroy {
  readonly abasValidas = ['acompanhamento', 'anexos', 'dadosAdicionais'] as const;

  @Input() aberto = false;
  @Input() titulo = '';
  @Input() formulario!: AcompanhamentoFormulario;
  @Input() componentes: AcompanhamentoComponenteCurricular[] = [];
  @Input() alunos: AcompanhamentoAluno[] = [];
  @Input() carregandoAlunos = false;
  @Input() destacarErroAlunos = false;
  @Input() somentePublicoAlvo = false;
  @Input() isTurmaEjaModular = false;
  @Input() mostrarErros = false;
  @Input() erros: string[] = [];
  @Input() salvando = false;
  @Input() formularioValidoParaSalvar = false;
  @Input() limiteObservacao = 32000;
  @Input() maxDate: Date = new Date();
  @Input() campoAdicional: Record<string, unknown> | Array<Record<string, unknown>> | null = null;
  @Input() carregandoCampoAdicional = false;

  @Output() fechar = new EventEmitter<void>();
  @Output() salvar = new EventEmitter<void>();
  @Output() excluir = new EventEmitter<void>();
  @Output() componenteChange = new EventEmitter<number | null>();
  @Output() dataChange = new EventEmitter<string>();
  @Output() alunoIdsChange = new EventEmitter<number[]>();
  @Output() somentePublicoAlvoChange = new EventEmitter<boolean>();

  alunoOptions: SearchMultiselectOption[] = [];
  componenteOptions: PrototypeSelectOption[] = [{ value: null, label: 'Selecione o(s) componente(s) curricular(es)' }];

  abaAtiva: 'acompanhamento' | 'anexos' | 'dadosAdicionais' = 'acompanhamento';
  anexos: AcompanhamentoAnexo[] = [];
  carregandoAnexos = false;
  erroEnvio: string | null = null;
  private uploadSubscriptions = new Map<number, Subscription>();
  private nextTempAnexoId = -1;

  get enviandoAnexo(): boolean {
    return this.uploadSubscriptions.size > 0;
  }

  /** true quando estamos editando um registro já persistido */
  get isEdicao(): boolean {
    return !!this.formulario?.id;
  }

  /** Lista de arquivos já carregados (para o pai sincronizar no save) */
  get arquivosAtuais(): AcompanhamentoAnexo[] {
    return this.anexos;
  }

  constructor(private anexoApiService: AcompanhamentoPedagogicoAnexoApiService) {}

  ngOnDestroy(): void {
    this.cancelarTodosUploads();
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['alunos']) {
      this.alunoOptions = this.alunos.map((aluno) => ({
        id: aluno.id,
        label: aluno.nomeSocial || aluno.nome
      }));
    }

    if (changes['componentes']) {
      this.componenteOptions = [
        { value: null, label: 'Selecione o componente curricular' },
        ...this.componentes.map((componente) => ({
          value: componente.id,
          label: componente.descricao
        }))
      ];
    }

    if (changes['aberto']) {
      if (this.aberto) {
        this.abaAtiva = 'acompanhamento';
        this.erroEnvio = null;
        this.carregarAnexos();
      }
    }
  }

  get observacaoCharCount(): number {
    return this.formulario?.observacao?.length || 0;
  }

  get dataSelecionada(): Date | null {
    if (!this.formulario?.data) {
      return null;
    }

    const [ano, mes, dia] = this.formulario.data.split('-').map(Number);
    if (!ano || !mes || !dia) {
      return null;
    }

    return new Date(ano, mes - 1, dia, 12, 0, 0);
  }

  get campoAlunosInvalido(): boolean {
    return this.destacarErroAlunos || (this.mostrarErros && !this.formulario?.alunoIds?.length);
  }

  carregarAnexos(): void {
    if (this.isEdicao) {
      this.anexos = [...(this.formulario?.arquivos ?? [])];
    } else {
      // Novo registro: sem arquivos até o save (fila local gerenciada em onFileSelected)
      this.anexos = [];
    }
  }

  onFilesSelected(files: File[]): void {
    files.forEach((file) => this.onFileSelected(file));
  }

  onFileSelected(file: File): void {
    this.erroEnvio = null;
    const tempId = this.nextTempAnexoId--;
    const anexoTemporario: AcompanhamentoAnexo = {
      id: tempId,
      nome: file.name,
      tamanho: file.size,
      pending: true,
      uploading: true
    };

    this.anexos = [...this.anexos, anexoTemporario];

    const subscription = this.anexoApiService.uploadAnexo(file).subscribe({
      next: (novoAnexo: AcompanhamentoAnexo) => {
        this.anexos = this.anexos.map((anexo) => anexo.id === tempId ? novoAnexo : anexo);
        this.uploadSubscriptions.delete(tempId);
      },
      error: () => {
        this.anexos = this.anexos.filter((anexo) => anexo.id !== tempId);
        this.erroEnvio = 'Erro ao enviar o arquivo. Tente novamente.';
        this.uploadSubscriptions.delete(tempId);
      }
    });

    this.uploadSubscriptions.set(tempId, subscription);
  }

  onRemoveAnexo(anexoId: number | string): void {
    if (typeof anexoId !== 'number') {
      return;
    }

    if (this.uploadSubscriptions.has(anexoId)) {
      this.uploadSubscriptions.get(anexoId)?.unsubscribe();
      this.uploadSubscriptions.delete(anexoId);
      this.anexos = this.anexos.filter((anexo) => anexo.id !== anexoId);
      return;
    }

    const anexo = this.anexos.find((a) => a.id === anexoId);
    this.anexos = this.anexos.filter((a) => a.id !== anexoId);

    this.anexoApiService.deleteAnexo(anexoId).subscribe({
      error: () => {
        if (anexo) {
          this.anexos = [...this.anexos, anexo];
        }
      }
    });
  }

  onDownloadAnexo(anexo: AnexoItem): void {
    if (typeof anexo.id !== 'number') {
      return;
    }

    this.anexoApiService.downloadAnexo(anexo.id, anexo.nome);
  }

  onBackdropClick(): void {
    this.fechar.emit();
  }

  onSubmit(): void {
    this.salvar.emit();
  }

  onComponenteChange(componenteId: number | null): void {
    this.componenteChange.emit(componenteId);
  }

  onComponenteSelectChange(value: number | string | null): void {
    this.componenteChange.emit(typeof value === 'number' ? value : null);
  }

  limparComponente(): void {
    this.componenteChange.emit(null);
  }

  onDateChange(data: Date | null): void {
    if (!data) {
      this.dataChange.emit('');
      return;
    }

    const ano = data.getFullYear();
    const mes = String(data.getMonth() + 1).padStart(2, '0');
    const dia = String(data.getDate()).padStart(2, '0');
    this.dataChange.emit(`${ano}-${mes}-${dia}`);
  }

  onObservacaoChange(valor: string): void {
    this.formulario.observacao = valor;
  }

  onPublicarChange(checked: boolean): void {
    this.formulario.publicar = checked;
  }

  onSomentePublicoAlvoChange(valor: boolean): void {
    this.somentePublicoAlvoChange.emit(valor);
  }

  onAbaAtivaChange(aba: string): void {
    if (this.abasValidas.includes(aba as typeof this.abasValidas[number])) {
      this.abaAtiva = aba as typeof this.abasValidas[number];
    }
  }



  onAlunoIdsChange(valores: Array<number | string>): void {
    this.alunoIdsChange.emit(valores.filter((valor): valor is number => typeof valor === 'number'));
  }

  private cancelarTodosUploads(): void {
    this.uploadSubscriptions.forEach((sub) => sub.unsubscribe());
    const idsEmUpload = new Set(this.uploadSubscriptions.keys());
    this.uploadSubscriptions.clear();
    this.anexos = this.anexos.filter((anexo) => !idsEmUpload.has(anexo.id));
  }
}